// --- External Dependencies
import React from 'react';
import {useSelector} from 'react-redux';
import {Col, Row} from 'antd';
// --- UI Components
import Typography from '@dtci/spellbook/Typography';
import Button from '@dtci/spellbook/Button';
import {Pagination} from '../../components/Pagination';
import {TagsGrid} from './components/grid';
import {TagsFilterForm} from './components/filter-form';
import TwoRowsLayout from './layout/two-row';
// --- Hooks
import {useTagsActions} from '../../store/tags/useTags';
import {useTagsDialog} from '../../components/dialogs/dialogs/tags';
import {useLoading} from '../../utils/hooks/useLoading';
// --- Utilities
import {getItemRange} from '../../utils/pagination';
import {filterCriteria} from '../../utils/filterCriteria';
import {exportToCsv} from '../../utils/exportToCsv';
// --- API
import {datastageApi} from '../../api';
import {tracker} from '../../analytics/snowplow';
import { getCurrentUser } from '../../utils/localStorageService';
/**
 * TagsPage Component
 * Renders the Tags page with filters, grid, and pagination.
 *
 * @returns {JSX.Element} The Tags Page component.
 */
export const TagsPage = () => {
    React.useEffect(() => {
        var currentUser = getCurrentUser();        
        tracker.setUserId(currentUser);
        tracker.trackPageView({ title: 'Tags' });
    }, []);
    const {setLoadingTrue, setLoadingFalse} = useLoading();
    const {updateTags} = useTagsActions();
    const {openTagsDialog} = useTagsDialog();
    const {totalPages, currentPage, filterForm, tagSort, tagRange, itemsPerPage, totalItems} =
        useSelector((state) => state.tags);

    // Handles page change in pagination
    const handleChange = async (pageNumber) => {
        if (pageNumber === currentPage) return;

        try {
            setLoadingTrue();
            const nextPageRange = getItemRange(pageNumber, itemsPerPage, totalItems);

            const data = await datastageApi.tags(
                filterCriteria(filterForm, '', 'tags'),
                nextPageRange,
                tagSort,
            );

            updateTags({
                tags: data?.content,
                totalItems: data?.totalElements,
                totalPages: data?.totalPages,
                currentPage: data?.pageable?.pageNumber,
                tagRange: nextPageRange,
            });
        } catch (error) {
            console.error('Error handling page change:', error);
        } finally {
            setLoadingFalse();
        }
    };

    // Export tags to a CSV file
    const handleExport = async () => {
        try {
            setLoadingTrue();

            const data = await datastageApi.tags(
                filterCriteria(filterForm, '', 'tags'),
                '0,999',
                tagSort,
            );

            exportToCsv(data?.content, 'tags');
        } catch (error) {
            console.error('Error exporting tags:', error);
        } finally {
            setLoadingFalse();
        }
    };

    // Refresh tags data
    const handleRefresh = async () => {
        try {
            setLoadingTrue();

            const data = await datastageApi.tags(
                filterCriteria(filterForm, '', 'tags'),
                tagRange,
                tagSort,
            );

            updateTags({
                tags: data?.content,
                totalItems: data?.totalElements,
                totalPages: data?.totalPages,
                currentPage: data?.pageable?.pageNumber,
            });
        } catch (error) {
            console.error('Error refreshing tags:', error);
        } finally {
            setLoadingFalse();
        }
    };

    // Opens the modal for creating a new tag
    const onOpenTagsModal = async () => {
        try {
            openTagsDialog('', 'create', async (tag) => {
                setLoadingTrue();
                await datastageApi.createTag(tag);
                await handleRefresh();
            });
        } catch (error) {
            console.error('Error opening tags modal:', error);
        } finally {

            setLoadingFalse()
        }
    };

    return (
        <>
            {/* Header Row with Title and Action Buttons */}
            <Row style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 20}}>
                <Col>
                    <Typography variant="T50" style={{marginLeft: 20}}>
                        Tags
                    </Typography>
                </Col>
                <Col style={{display: 'flex', gap: 8}}>
                    <Button
                        iconType="fa-solid fa-arrow-down-to-line"
                        labelText="Export"
                        styleType="link"
                        onClick={handleExport}
                    >
                        Export
                    </Button>
                    <Button
                        iconType="fa-solid fa-plus"
                        labelText="Create"
                        styleType="link"
                        onClick={onOpenTagsModal}
                    >
                        Create
                    </Button>
                    <Button
                        iconType="fa-solid fa-rotate"
                        labelText="Refresh"
                        styleType="link"
                        onClick={handleRefresh}
                    >
                        Refresh
                    </Button>
                </Col>
            </Row>

            {/* Layout with Filter Form and Grid */}
            <TwoRowsLayout>
                <main data-position="top">
                    <TagsFilterForm/>
                </main>
                <main data-position="bottom">
                    <TagsGrid/>
                </main>
            </TwoRowsLayout>

            {/* Pagination Component */}
            <Pagination
                pageCount={totalPages}
                onPageChange={(e) => handleChange(e.selected)}
                previousLabel="←"
                nextLabel="→"
                forcePage={currentPage}
            />
        </>
    );
};
